import React from 'react';


interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  itemsPerPage: number;
  itemLabel?: string;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  itemLabel = 'leads',
  className = ''
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (currentPage <= 3) {
        // Near start: show 1, 2, 3, ..., last
        for (let i = 1; i <= 3; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near end: show 1, ..., last-2, last-1, last
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle: show 1, ..., current-1, current, current+1, ..., last
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const buttonBaseStyles: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  };

  const activeButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    background: 'linear-gradient(135deg, #0ea5e9, #0369a1)',
    color: 'white',
    boxShadow: '0 4px 16px rgba(14, 165, 233, 0.3)',
    border: '1px solid rgba(14, 165, 233, 0.4)',
    transform: 'translateY(-1px)',
  };

  const inactiveButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#374151',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(209, 213, 219, 0.8)',
  };

  const disabledButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    background: 'rgba(156, 163, 175, 0.3)',
    color: 'rgba(107, 114, 128, 0.8)',
    cursor: 'not-allowed',
    boxShadow: 'none',
    border: '1px solid rgba(156, 163, 175, 0.3)',
    transform: 'none',
  };

  const navigationButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#374151',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(209, 213, 219, 0.8)',
    padding: '8px 16px',
    minWidth: 'auto',
  };

  return (
    <div className={`pagination-container ${className}`} style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      padding: '20px',
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(8px)',
      borderRadius: '16px',
      border: '1px solid rgba(209, 213, 219, 0.6)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    }}>
      {/* Items info */}
      <div style={{
        padding: '8px 16px',
        background: 'rgba(14, 165, 233, 0.1)',
        borderRadius: '8px',
        border: '1px solid rgba(14, 165, 233, 0.2)',
        color: '#0c4a6e',
        fontSize: '13px',
        fontWeight: '500',
        textAlign: 'center',
        minWidth: '180px',
      }}>
        Showing {startItem} to {endItem} of {totalItems} {itemLabel}
      </div>

      {/* Pagination controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={currentPage === 1 ? disabledButtonStyles : navigationButtonStyles}
          onMouseEnter={(e) => {
            if (currentPage > 1) {
              e.currentTarget.style.background = 'rgba(14, 165, 233, 0.1)';
              e.currentTarget.style.border = '1px solid rgba(14, 165, 233, 0.3)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage > 1) {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.border = '1px solid rgba(209, 213, 219, 0.8)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          ← Previous
        </button>

        {/* Page numbers */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span style={{
                  padding: '8px 6px',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontWeight: '500',
                }}>
                  ...
                </span>
              ) : (
                <button
                  onClick={() => onPageChange(page as number)}
                  style={currentPage === page ? activeButtonStyles : inactiveButtonStyles}
                  onMouseEnter={(e) => {
                    if (currentPage !== page) {
                      e.currentTarget.style.background = 'rgba(14, 165, 233, 0.1)';
                      e.currentTarget.style.border = '1px solid rgba(14, 165, 233, 0.3)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== page) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                      e.currentTarget.style.border = '1px solid rgba(209, 213, 219, 0.8)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={currentPage === totalPages ? disabledButtonStyles : navigationButtonStyles}
          onMouseEnter={(e) => {
            if (currentPage < totalPages) {
              e.currentTarget.style.background = 'rgba(14, 165, 233, 0.1)';
              e.currentTarget.style.border = '1px solid rgba(14, 165, 233, 0.3)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage < totalPages) {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.border = '1px solid rgba(209, 213, 219, 0.8)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          Next →
        </button>
      </div>

      {/* Page indicator */}
      <div style={{
        padding: '6px 12px',
        background: 'rgba(14, 165, 233, 0.1)',
        borderRadius: '16px',
        border: '1px solid rgba(14, 165, 233, 0.2)',
        color: '#0c4a6e',
        fontSize: '11px',
        fontWeight: '600',
        textAlign: 'center',
      }}>
        Page {currentPage} of {totalPages}
      </div>

      <style>{`
        .pagination-container {
          animation: fadeInUp 0.4s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .pagination-container button:active {
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
};
